import type { Lang } from "../content/types";
import { addDays, daysBetween, weekdayIndex } from "../lib/date";
import { emptyRingDay, type Profile, type RingDay } from "../state/profile";

export const REST_DAY_BANK_CAP = 2;
export const REST_DAY_EVERY_CLOSED_RINGS = 7;
export const REPAIR_COOLDOWN_DAYS = 30;

export type StreakEvent =
  | { type: "streak"; days: number }
  | { type: "rest-day-earned" }
  | { type: "rest-day-consumed"; day: string }
  | { type: "streak-broken"; lostStreak: number }
  | { type: "streak-repaired"; days: number }
  | { type: "ring-closed" };

export type TouchOptions = { closesRing: boolean; lang?: Lang };

/** A read-only look at what touching `today` would do, without mutating —
    lets the UI show "your streak ended yesterday, train today to restore
    it" before a session even starts. */
export type StreakPreview = {
  paused: boolean;
  wouldBreak: boolean;
  lostStreak: number;
  repairEligible: boolean;
  restDaysToConsume: number;
};

function reconcileGap(profile: Profile, today: string): {
  restDays: number;
  broke: boolean;
  lostStreak: number;
  consumedDays: string[];
} {
  const s = profile.streak;
  const consumedDays: string[] = [];
  if (!s.lastDay || s.lastDay === today) return { restDays: s.restDays, broke: false, lostStreak: 0, consumedDays };

  const gap = daysBetween(s.lastDay, today);
  const missed = gap - 1;
  let restDays = s.restDays;
  for (let i = 0; i < missed; i++) {
    if (restDays > 0) {
      restDays -= 1;
      consumedDays.push(addDays(s.lastDay, i + 1));
    } else {
      return { restDays, broke: true, lostStreak: s.current, consumedDays };
    }
  }
  return { restDays, broke: false, lostStreak: 0, consumedDays };
}

export function previewStreak(profile: Profile, today: string): StreakPreview {
  const s = profile.streak;
  if (s.pausedUntil && today <= s.pausedUntil) {
    return { paused: true, wouldBreak: false, lostStreak: 0, repairEligible: false, restDaysToConsume: 0 };
  }
  if (s.lastDay === today) {
    return { paused: false, wouldBreak: false, lostStreak: 0, repairEligible: false, restDaysToConsume: 0 };
  }
  const outcome = reconcileGap(profile, today);
  const repairEligible =
    outcome.broke &&
    (!s.repairLastUsed || daysBetween(s.repairLastUsed, today) >= REPAIR_COOLDOWN_DAYS);
  return {
    paused: false,
    wouldBreak: outcome.broke,
    lostStreak: outcome.lostStreak,
    repairEligible,
    restDaysToConsume: outcome.consumedDays.length,
  };
}

/** The single streak/ring mutator. Call once per completed session. */
export function touchDay(profile: Profile, today: string, opts: TouchOptions): { profile: Profile; events: StreakEvent[] } {
  const events: StreakEvent[] = [];
  const p: Profile = structuredClone(profile);
  const s = p.streak;

  const ring: RingDay = p.rings[today] ?? emptyRingDay();
  if (opts.lang && !ring.langs.includes(opts.lang)) ring.langs = [...ring.langs, opts.lang];

  if (s.pausedUntil && today <= s.pausedUntil) {
    if (opts.closesRing && !ring.closed) {
      ring.closed = true;
      events.push({ type: "ring-closed" });
    }
    p.rings[today] = ring;
    return { profile: p, events };
  }

  if (s.lastDay === today) {
    if (opts.closesRing && !ring.closed) {
      ring.closed = true;
      events.push({ type: "ring-closed" });
      awardRestDayIfEarned(s, events);
    }
    p.rings[today] = ring;
    return { profile: p, events };
  }

  const outcome = reconcileGap(p, today);
  for (const day of outcome.consumedDays) events.push({ type: "rest-day-consumed", day });
  s.restDays = outcome.restDays;

  if (outcome.broke) {
    events.push({ type: "streak-broken", lostStreak: outcome.lostStreak });
    const repairEligible = !s.repairLastUsed || daysBetween(s.repairLastUsed, today) >= REPAIR_COOLDOWN_DAYS;
    if (repairEligible) {
      s.current = outcome.lostStreak + 1;
      s.repairLastUsed = today;
      events.push({ type: "streak-repaired", days: s.current });
    } else {
      s.current = 1;
    }
    s.consecutiveClosedRings = 0;
    s.brokenAt = null;
  } else {
    s.current = s.lastDay ? s.current + 1 : 1;
  }

  s.longest = Math.max(s.longest, s.current);
  s.lastDay = today;
  events.push({ type: "streak", days: s.current });

  if (opts.closesRing) {
    ring.closed = true;
    events.push({ type: "ring-closed" });
    awardRestDayIfEarned(s, events);
  }
  p.rings[today] = ring;

  return { profile: p, events };
}

function awardRestDayIfEarned(s: Profile["streak"], events: StreakEvent[]): void {
  s.consecutiveClosedRings += 1;
  if (s.consecutiveClosedRings % REST_DAY_EVERY_CLOSED_RINGS === 0 && s.restDays < REST_DAY_BANK_CAP) {
    s.restDays += 1;
    events.push({ type: "rest-day-earned" });
  }
}

export function pauseTraining(profile: Profile, today: string, days: number): Profile {
  const p = structuredClone(profile);
  p.streak.pausedUntil = addDays(today, Math.max(0, days) - 1);
  return p;
}

/** Count of closed rings within the current calendar week (Sun-Sat) that
    contains `today` — drives the 7-segment weekly Ring on Today. */
export function weeklyClosedCount(profile: Profile, today: string): { filled: number; segments: 7 } {
  const dow = weekdayIndex(today); // 0 = Sunday
  let filled = 0;
  for (let i = 0; i < 7; i++) {
    const day = addDays(today, i - dow);
    if (profile.rings[day]?.closed) filled += 1;
  }
  return { filled, segments: 7 };
}
